import { ArrowRight, User, Code, Brain, Zap, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePromotional } from '../../contexts/PromotionalContext';
import { buildUdemyCourseUrl } from '../../utils/udemyLinks';
import CouponBanner from '../ui/CouponBanner';

const CourseRecommendationSection = () => {
  const { config, isActive } = usePromotional();
  
  const personas = [
    {
      id: 1,
      title: "New to AI Testing",
      description: "Manual tester or QA engineer with little to no AI experience",
      icon: User,
      color: "from-blue-500 to-cyan-500",
      bgColor: "bg-blue-50 border-blue-200",
      textColor: "text-blue-700",
      recommendedCourses: ["ZERO Code AI Tests", "Using Gen AI & AI Agent in Software Automation Testing"],
      nextSteps: ["No-code AI test automation", "Learn AI fundamentals", "Basic prompt engineering", "Simple automation integration"],
      coursePath: "Start with zero-code testing → Build foundation → Grow expertise",
      bundleRecommendation: "QA Beginner Bundle",
      udemy: ["zero-code-ai-testing", "generative-ai-in-software-automation-testing"]
    },
    {
      id: 2, 
      title: "Automation Engineer",
      description: "Experienced with test automation, ready to integrate AI capabilities",
      icon: Code,
      color: "from-green-500 to-emerald-500", 
      bgColor: "bg-green-50 border-green-200",
      textColor: "text-green-700",
      recommendedCourses: ["ZERO Code AI Tests", "Using Gen AI & AI Agent in Software Automation Testing", "Test AI & LLM Apps with DeepEval"],
      nextSteps: ["No-code AI test automation", "AI testing fundamentals", "DeepEval framework mastery", "Advanced AI validation techniques"],
      coursePath: "Zero-code testing + Foundation + Framework expertise → Advanced testing",
      bundleRecommendation: "QA Professional Bundle",
      udemy: ["zero-code-ai-testing", "generative-ai-in-software-automation-testing", "ai-testing-deepeval-ragas-ollama"]
    },
    {
      id: 3,
      title: "AI Enthusiast",
      description: "Familiar with AI concepts, want to specialize in testing AI systems",
      icon: Brain,
      color: "from-purple-500 to-pink-500",
      bgColor: "bg-purple-50 border-purple-200", 
      textColor: "text-purple-700",
      recommendedCourses: ["ZERO Code AI Tests", "Using Gen AI & AI Agent in Software Automation Testing", "Test AI & LLM Apps with DeepEval", "Build Agentic AI & Multi-Agent System for Software Testing", "Red Teaming AI Apps with Promptfoo"],
      nextSteps: ["No-code AI test automation", "AI testing foundation", "Framework expertise", "AI agent development & testing", "AI red teaming and RAG security"],
      coursePath: "Zero-code testing + Foundation + Frameworks + Development + Security → Complete AI testing",
      bundleRecommendation: "AI Enthusiast Path",
      udemy: ["zero-code-ai-testing", "generative-ai-in-software-automation-testing", "ai-testing-deepeval-ragas-ollama", "build-ai-agent-chatbot-rag-langchain-local-llm", "ai-red-teaming-promptfoo-rag-chatbots-agents"]
    },
    {
      id: 4,
      title: "Senior Engineer",
      description: "Experienced professional looking to master advanced AI testing architectures",
      icon: Zap,
      color: "from-orange-500 to-red-500",
      bgColor: "bg-orange-50 border-orange-200",
      textColor: "text-orange-700", 
      recommendedCourses: ["Complete Learning Path", "GenAI Multi-Agent Systems", "LLM Fine-Tuning for Beginners: HuggingFace & Unsloth"],
      nextSteps: ["Enterprise AI systems", "Complex multi-agent workflows", "Advanced model fine-tuning"],
      coursePath: "All foundations + Advanced systems + Model development",
      bundleRecommendation: "SDET + Dev Master Bundle",
      udemy: ["generative-ai-in-software-automation-testing", "ai-testing-deepeval-ragas-ollama", "build-ai-agent-chatbot-rag-langchain-local-llm", "genai-multi-agent-software-testing", "ai-with-huggingface"]
    }
  ];

  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Which Course Should <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">You Start With?</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose your starting point based on your current experience and career goals. Each path leads to AI testing mastery.
          </p>
        </div>

        {/* Persona Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {personas.map((persona, index) => {
            const Icon = persona.icon;
            return (
              <div 
                key={persona.id} 
                className={`${persona.bgColor} rounded-2xl p-8 border-2 transition-shadow duration-300 hover:shadow-xl animate-fade-in group cursor-pointer will-change-auto`}
                style={{animationDelay: `${index * 200}ms`}}
              >
                <div className="flex items-start space-x-6 mb-6">
                  <div className={`p-4 rounded-xl bg-gradient-to-r ${persona.color} shadow-lg flex-shrink-0 will-change-transform`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{persona.title}</h3>
                    <p className="text-gray-700 leading-relaxed">{persona.description}</p>
                  </div>
                </div>

                {/* Recommended Courses */}
                <div className={`${persona.bgColor.replace('bg-', 'bg-white border-')} rounded-xl p-4 mb-6 border`}>
                  <h4 className={`font-semibold ${persona.textColor} mb-3`}>Recommended Starting Courses:</h4>
                  <div className="space-y-2">
                    {persona.recommendedCourses.map((course, courseIndex) => (
                      <div key={courseIndex} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span className="text-gray-900 font-medium">{course}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Learning Path:</span> {persona.coursePath}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Best Bundle:</span> {persona.bundleRecommendation}
                    </p>
                  </div>
                </div>

                {/* Next Steps */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">What You'll Learn First:</h4>
                  <div className="space-y-2">
                    {persona.nextSteps.map((step, stepIndex) => (
                      <div key={stepIndex} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Coupon Code Display */}
                {config.isEnabled && isActive && (
                  <div className="mb-4">
                    <CouponBanner
                      code={config.couponCode}
                      description={config.description}
                      variant="card"
                      showCopy={false}
                      className="text-xs"
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-3">
                  <a
                    href={buildUdemyCourseUrl(persona.udemy[0], config, isActive)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r ${persona.color} text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 group/btn will-change-auto`}
                  >
                    Start First Course
                    <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </a>
                  <Link
                    to={`/bundle/${persona.id === 1 ? 'new-to-ai' : persona.id === 2 ? 'automation-engineer' : persona.id === 3 ? 'ai-enthusiast' : 'senior-engineer'}`}
                    className="w-full inline-flex items-center justify-center px-6 py-2 border-2 border-gray-300 text-gray-700 hover:border-green-500 hover:text-green-600 font-semibold rounded-xl transition-colors duration-300"
                  >
                    View Persona Bundle
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Not Sure Section */}
        <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-gray-600 to-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Brain className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Not Sure Where to Start?</h3>
            <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Explore our Learn Path for a comprehensive step-by-step journey to master AI, test AI systems with DeepEval & RAGAs, 
              leverage HuggingFace Evaluate, and supercharge your existing automation testing with the power of AI.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/paths"
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-6 py-3 font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
            >
              Explore Learn Path
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <Link
              to="/courses"
              className="bg-white border-2 border-gray-300 text-gray-700 hover:border-green-500 hover:text-green-600 px-6 py-3 font-semibold rounded-xl transition-colors duration-300"
            >
              Browse All Courses
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CourseRecommendationSection;
